import { useEffect, useRef, useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Checkbox } from '@opendoor/atoms/checkbox';
import { Input } from '@opendoor/atoms/input';
import { Text } from '@opendoor/atoms/text';
import { useAuth } from '@opendoor/auth-fe/context';
import { IconCircleCheck } from '@tabler/icons-react';
import type { PropertyDetailsType } from 'components/marketplace/property/types';
import {
  LISTING_AVAILABILITY_EMAIL_CONSENT_VERSION,
  LISTING_AVAILABILITY_MARKETING_OPT_IN_LABEL,
  LISTING_AVAILABILITY_MARKETING_OPT_IN_SUBTEXT,
} from 'components/marketplace/shared/availableSoon';

type PdpEmailCaptureCardProperty = Pick<PropertyDetailsType, 'addressToken'>;

type SubmitState = 'idle' | 'submitting' | 'success';

// Same-origin REST, like the home-value capture endpoint; the zone origin
// routes /api/v1 to web.
const CAPTURE_ENDPOINT = '/api/v1/marketing/listing-availability-email-capture-request';

const SUCCESS_BODY = 'We’ll email you when this home becomes available.';

// Client-side gate only; the endpoint re-validates, so this exists to give an
// instant invalid-state response rather than a round trip.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const PdpEmailCaptureCard = ({ property }: { property: PdpEmailCaptureCardProperty }) => {
  const { authentication, user } = useAuth();
  const isLoggedIn = authentication.state === 'authenticated';
  const accountEmail = isLoggedIn ? (user?.email ?? null) : null;

  // Null until the viewer types, so late-resolving auth prefills without
  // clobbering input; the typed value wins once present.
  const [typedEmail, setTypedEmail] = useState<string | null>(null);
  const email = typedEmail ?? accountEmail ?? '';
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [state, setState] = useState<SubmitState>('idle');
  // invalidEmail drives aria-invalid and the red field treatment, so request
  // failures must not mark a valid address invalid.
  const [error, setError] = useState<{ message: string; invalidEmail: boolean } | null>(null);
  const successRef = useRef<HTMLParagraphElement>(null);

  // The submit button unmounts on success; land focus on the confirmation
  // instead of dropping it to the document body.
  useEffect(() => {
    if (state === 'success') {
      successRef.current?.focus();
    }
  }, [state]);

  const onEmailMe = async () => {
    if (!EMAIL_PATTERN.test(email)) {
      setError({ message: 'Enter a valid email address.', invalidEmail: true });
      return;
    }

    setError(null);
    setState('submitting');
    try {
      const response = await fetch(CAPTURE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          address_token: property.addressToken,
          disclosure_version: LISTING_AVAILABILITY_EMAIL_CONSENT_VERSION,
          email,
          marketing_opt_in: marketingOptIn,
        }),
      });

      if (response.ok) {
        setState('success');
      } else if (response.status === 422) {
        setState('idle');
        setError({ message: 'Enter a valid email address.', invalidEmail: true });
      } else if (response.status === 409) {
        // Terminal without a reload: this bundle ships an outdated consent
        // version, which only the next page load can pick up.
        setState('idle');
        setError({
          message: 'This page is out of date. Refresh and try again.',
          invalidEmail: false,
        });
      } else if (response.status === 429) {
        setState('idle');
        setError({
          message: 'Too many attempts. Wait a minute and try again.',
          invalidEmail: false,
        });
      } else {
        setState('idle');
        setError({ message: 'Something went wrong. Try again.', invalidEmail: false });
      }
    } catch {
      setState('idle');
      setError({ message: 'Something went wrong. Try again.', invalidEmail: false });
    }
  };

  return (
    <Col className="gap-4">
      <Text as="p" className="text-subheader-xs text-od-content-primary">
        Get notified when this listing becomes available
      </Text>
      {/* Always mounted for the live announcement, but out of flow when empty
          so it leaves no visual gap above the form. */}
      <Row
        className={state === 'success' ? 'items-center gap-2' : 'absolute h-0 overflow-hidden'}
        aria-live="polite"
      >
        {state === 'success' && (
          <>
            <IconCircleCheck size={20} className="text-od-content-success" aria-hidden />
            <Text
              as="p"
              ref={successRef}
              tabIndex={-1}
              className="text-body-md text-od-content-primary"
            >
              {SUCCESS_BODY}
            </Text>
          </>
        )}
      </Row>
      {state !== 'success' && (
        <>
          <Input
            type="email"
            value={email}
            onChange={(event) => {
              setTypedEmail(event.target.value);
              setError(null);
            }}
            size="xs"
            placeholder="you@email.com"
            className="h-14 w-full"
            aria-label="Email address"
            aria-describedby={
              error
                ? 'pdp-email-capture-opt-in pdp-email-capture-error'
                : 'pdp-email-capture-opt-in'
            }
            aria-invalid={error?.invalidEmail === true}
            error={error?.invalidEmail === true}
            analyticsName="cosmos-marketplace-email-me-email"
          />
          {error != null && (
            <Text
              as="p"
              id="pdp-email-capture-error"
              role="alert"
              className="text-body-sm text-od-content-error"
            >
              {error.message}
            </Text>
          )}
          <Box className="flex-wrap items-start gap-2 px-1.5 py-0 [&_[role='checkbox']]:shrink-0">
            <Checkbox
              checked={marketingOptIn}
              onCheckedChange={(checked) => setMarketingOptIn(checked === true)}
              caption={LISTING_AVAILABILITY_MARKETING_OPT_IN_LABEL}
              value={String(marketingOptIn)}
              id="pdp-email-capture-opt-in"
              analyticsName="cosmos-marketplace-email-me-opt-in"
            />
            <Text as="p" className="text-body-sm text-od-content-secondary">
              {LISTING_AVAILABILITY_MARKETING_OPT_IN_SUBTEXT}
            </Text>
          </Box>
          <Button
            variant="primary"
            size="lg"
            onClick={onEmailMe}
            loading={state === 'submitting'}
            className="w-full"
            aria-label="Email me when available"
            analyticsName="cosmos-marketplace-email-me-cta"
          >
            Email me when available
          </Button>
        </>
      )}
    </Col>
  );
};
